import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDashboardService } from '../services/user-dashboard.service';
import { RoleViewService } from '../services/role-view.service';
import { CreateProjectService } from '../services/create-project.service';
import { DashboardService } from '../services/dashboard.service';
import { NotificationService } from '../services/notification.service';
import { renderAsync } from 'docx-preview';
import * as XLSX from 'xlsx';
import { WebViewerInstance } from '@pdftron/webviewer';
import { AppMaterialModule } from '../app-modules/app-material.module';
import { UtilModule } from '../app-modules/util.module';
import { TutorialVideoStep, TutorialVideoWidgetComponent } from '../components/tutorial-video-widget/tutorial-video-widget.component';
import { USER_WORKFLOW_TUTORIAL_FLOW } from '../components/tutorial-video-widget/tutorial-video-flows';

type ProjectStatus =
  | 'Unassigned'
  | 'Assigned'
  | 'Submitted'
  | 'In Progress'
  | 'Pending RRE Review'
  | 'Pending RAES Review'
  | 'Completed';

type DocumentMarkStatus = 'No Change Required' | 'Not Responsible' | 'Completed';

type PreviewType = 'pdf' | 'docx' | 'xls' | 'image' | 'unsupported' | null;

type SubmittedProjectStatus = 'no-change' | 'not-responsible' | 'completed' | '';

type ApprovalDialogMode = 'approved' | 'rework' | null;

interface FileVersion {
  versionNumber: string;
  uploadedBy: string;
  uploadDate: string;
  uploadTime: string;
  isLatest: boolean;
  fileVersioningId?: string;
  fileName?: string;
}

interface UploadedFileData {
  fileId: string;
  fileName: string;
  versions: FileVersion[];
  file_versioning_id?: string;
  file_name?: string;
  document_status?: string;
  raes_uploaded_file_id?:number;
}

interface TeamMember {
  id: string;
  name: string;
  jobCode: string;
  jobTitle: string;
  email: string;
}

interface ProjectRecord {
  id: number;
  name: string;
  collection: string;
  subCollections: string[];
  departmentId: string;
  departmentAssigned: string;
  contactPerson: string;
  dueDate: string;
  documentNumber: string;
  status: ProjectStatus;
  assignedTo?: string;
  managerId?: string;
  managerName?: string;
  managerEmailId?: string;
  subTaskStatus?: string;
  projectSubTaskId?: string;
  noDocumentUploadsStatus?: string;
  adminDocuments: string[];
  uploadedDocuments: string[];
  uploadedFilesData: UploadedFileData[];
  uploadedDocumentsByAdminDoc?: Record<string, string[]>;
  documentStatusByAdminDoc?: Record<string, DocumentMarkStatus | ''>;
  assignedToEmail?: string;
  email_body?: string;
  subject?: string;
}
interface Activity {
  activity_type?: string;
  activityType?: string;
  activity_description?: string;
}

const NO_CHANGE_REQUIRED: DocumentMarkStatus = 'No Change Required';
const NOT_RESPONSIBLE: DocumentMarkStatus = 'Not Responsible';
const COMPLETED: DocumentMarkStatus = 'Completed';
const IN_PROGRESS = 'in progress';
const RRE_REVIEW = 'RRE Review';
const PROJECT_ASSIGNED = 'Project Assigned';
const DOCUMENTS_UPLOADED = 'Documents Uploaded';
const RAES_REVIEW = 'RAES Review';
const IN_PROGRESS_STATUS: ProjectStatus = 'In Progress';
const PENDING_RRE_REVIEW: ProjectStatus = 'Pending RRE Review';
const PENDING_RAES_REVIEW: ProjectStatus = 'Pending RAES Review';

@Component({
  selector: 'app-user-project-detail',
  standalone: true,
  imports: [
    AppMaterialModule,
    UtilModule,
    TutorialVideoWidgetComponent
  ],
  templateUrl: './user-project-detail.component.html',
  styleUrl: './user-project-detail.component.css'
})
export class UserProjectDetailComponent implements OnInit, OnDestroy {
  readonly userWorkflowTutorialSequence: TutorialVideoStep[] = USER_WORKFLOW_TUTORIAL_FLOW;
  userId = 'u1';
  projectId = 0;
  fromManageApproval = false;
  selectedMemberId = '';
  selectedManagerId = '';
  selectedManagerName = '';
  assignmentMode: 'self' | 'others' | '' = '';
  managerAssignmentMode: 'self' | 'others' | '' = '';
  showAssignmentPanel = false;
  showManagerAssignmentPanel = false;
  documentMarkStatuses: DocumentMarkStatus[] = [NO_CHANGE_REQUIRED, NOT_RESPONSIBLE, COMPLETED];
  departmentId = '';
  expandedFileId: string | null = null;
  currentFileUploadId: string | null = null;
  currentFileUploadType: 'reupload' | 'new-version' | '' = '';
  documentStatusPending: Record<string, DocumentMarkStatus | ''> = {};
  documentStatusEditing: Record<string, boolean> = {};
  activityStreamData: any[] | null = null;
  isActivityTimelineExpanded = false;
  newActivityComment = '';
  isLoading = true;
  private debounceTimer: any;
  // Stepper state
  activeStepperStep = 1;

  // Preview properties
  @ViewChild('docxContainer') docxContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('webviewerContainer', { static: false }) webviewerContainerRef!: ElementRef<HTMLDivElement>;
  isPreviewOpen = false;
  previewTitle = '';
  previewUrl: string | null = null;
  previewType: PreviewType = null;
  private _wvInstance: WebViewerInstance | null = null;
  workbookCache: any = null;
  sheetNames: string[] = [];
  selectedSheetIndex = 0;
  excelHtml = '';
  private _objectUrls: string[] = [];
  
  // History tracking for preview
  private _previewHistoryState = false;
  private _onPopState: null | ((ev: PopStateEvent) => void) = null;
  private _ignoreNextPop = false;
  
  @ViewChild('documentFileInput') documentFileInput!: ElementRef<HTMLInputElement>;
  workflowSteps: string[] = [
    PROJECT_ASSIGNED,
    DOCUMENTS_UPLOADED,
    RRE_REVIEW,
    RAES_REVIEW,
    COMPLETED
  ];

  departmentMembers: TeamMember[] = [];

  projects: ProjectRecord[] = [];
  // Approval / Rejection dialog state
  showApprovalDialog = false;
  approvalDialogMode: ApprovalDialogMode = null;
  approvalComment = '';
  showApprovalResultDialog = false;
  approvalResultType: 'approved' | 'rework' | null = null;
  showAlreadyApprovedDialog = false;
  alreadyActionedType: 'approved' | 'rework' | null = null;
  // True when a Manage Approval link is reopened after the request was already
  // approved/reworked - the page is then shown exactly as it looked on first open.
  alreadyActionedOnOpen = false;
  private hasCheckedAlreadyApproved = false;

  // Update Project Status state
  selectedProjectStatus: SubmittedProjectStatus = '';
  updateStatusNotes = '';
  isUpdateStatusExpanded = false;
  submittedProjectStatus: SubmittedProjectStatus = '';
  statusSubmittedForApproval = false;
  showStatusSuccessDialog = false;
  pendingStatusUpdate = false;
  highlightApprovalPanel = false;

  
  showDeleteFileDialog = false;
  deleteFileDetails: { fileVersioningId: string; fileName: string } = { fileVersioningId: '', fileName: '' };

 
  successDialogTitle = '';
  successDialogMessage = '';
  showNextStep = false;
  highlightStep3 = false;
  pendingDocumentStatusSuccess = false;
  pendingDocumentStatusIsLast = false;
  pendingAssigneeSuccess = false;
  pendingDeleteSuccess = false;
  deletedFileName = '';
  isFirstAssignment = false;
  assignedMemberName = '';
  originalDepartmentMembers!: TeamMember[];
  rreApiMembersByEmail!: Map<string, TeamMember>;

  toggleUpdateStatusPanel(): void {
    this.isUpdateStatusExpanded = !this.isUpdateStatusExpanded;
  }

  selectProjectStatus(status: 'no-change' | 'not-responsible' | 'completed'): void {
    this.selectedProjectStatus = status;
  }

  cancelProjectStatusUpdate(): void {
    this.selectedProjectStatus = '';
    this.updateStatusNotes = '';
    this.isUpdateStatusExpanded = false;
  }

  dismissStatusSuccessDialog(): void {
    this.showStatusSuccessDialog = false;
    if (this.showNextStep) {
      // Advance stepper to next step (RRE Review Assignment)
      this.advanceToNextIncompleteStep();
    }
  }

  goToRreReviewStep(): void {
    this.showStatusSuccessDialog = false;
    this.activeStepperStep = 3;
    this.showManagerAssignmentPanel = true;
    this.highlightStep3 = true;
    setTimeout(() => {
      document.getElementById('stepper-step-3')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    setTimeout(() => {
      this.highlightStep3 = false;
    }, 3000);
  }

  showDocumentUploadSuccess(documentName: string): void {
    this.successDialogTitle = 'Document Uploaded Successfully';
    this.successDialogMessage = `<strong>${documentName}</strong> has been uploaded. You can review the document and update the status.`;
    this.showNextStep = false;
    this.showStatusSuccessDialog = true;
  }

  showDocumentStatusUpdatedSuccess(isLastDocument = false): void {
    this.successDialogTitle = 'Document Status Updated Successfully';
    if (isLastDocument) {
      this.successDialogMessage = 'All documents have been reviewed.';
      this.showNextStep = true;
    } else {
      this.successDialogMessage = 'You can review and update status for other documents.';
      this.showNextStep = false;
    }
    this.showStatusSuccessDialog = true;
  }

  showAssigneeSuccess(isFirstAssignment = false, memberName = ''): void {
    if (isFirstAssignment) {
      this.successDialogTitle = 'Task has been Assigned';
      this.successDialogMessage = memberName ? `${memberName} has been assigned successfully.` : 'Assignee has been assigned successfully.';
    } else {
      this.successDialogTitle = 'Assignee Updated';
      this.successDialogMessage = memberName ? `The assignee has been updated to ${memberName} successfully.` : 'The assignee has been updated successfully.';
    }
    this.showNextStep = false;
    this.showStatusSuccessDialog = true;
  }

  showTaskStatusSuccess(): void {
    this.successDialogTitle = 'Document Status Updated';
    this.successDialogMessage = `Your document status has been updated to <strong>${this.getSubmittedStatusLabel()}</strong>.`;
    this.showNextStep = true;
    this.showStatusSuccessDialog = true;
  }

  getSubmittedStatusLabel(): string {
    const statusLabelMap: Record<string, string> = {
      'no-change': NO_CHANGE_REQUIRED,
      'not-responsible': NOT_RESPONSIBLE,
      'completed': COMPLETED
    };
    return statusLabelMap[this.submittedProjectStatus] || '';
  }

  getSubmittedStatusClass(): string {
    const statusClassMap: Record<string, string> = {
      'no-change': 'badge-no-change-required',
      'not-responsible': 'badge-not-responsible',
      'completed': 'badge-completed'
    };
    return statusClassMap[this.submittedProjectStatus] || '';
  }

  submitProjectStatusUpdate(skipDialog = false): void {
    if (!this.selectedProjectStatus) {
      return;
    }

    const statusMap: Record<string, string> = {
      'no-change': NO_CHANGE_REQUIRED,
      'not-responsible': NOT_RESPONSIBLE,
      'completed': COMPLETED
    };

    const activityTypeMap: Record<string, string> = {
      'no-change': NO_CHANGE_REQUIRED,
      'not-responsible': NOT_RESPONSIBLE,
      'completed': COMPLETED
    };

    // No documents to review means this single action completes Step 2 - always move
    // straight to Pending RRE Review (defaulting the RRE reviewer to the Step 1 assignee
    // when none has been explicitly chosen yet), matching the per-document flow.
    const defaultRreEmail = this.projectDetail?.managerEmailId || this.selectedManagerId || '';
    const defaultRreName = this.projectDetail?.managerName || this.selectedManagerName || '';

    const payload = {
      projectId: this.projectId,
      projectName: this.projectDetail.name,
      departmentName: this.projectDetail.departmentAssigned,
      departmentId: this.projectDetail?.departmentId || this.departmentId,
      updatedBy: this.userDashboardService.userName,
      status: PENDING_RRE_REVIEW,
      projectSubTaskId: this.projectDetail?.projectSubTaskId,
      assigneeName: this.projectDetail?.assignedTo || this.selectedMemberName,
      assigneeId: this.projectDetail?.assignedToEmail || this.selectedMemberId,
      assigneeEmailId: this.projectDetail?.assignedToEmail || this.selectedMemberId,
      userComments: this.updateStatusNotes || '',
      activityType: activityTypeMap[this.selectedProjectStatus],
      activityDescription: `No documents to review. Status updated to ${statusMap[this.selectedProjectStatus]}. ${this.updateStatusNotes ? 'Notes: ' + this.updateStatusNotes : ''}`,
      rreEmailAddress: defaultRreEmail,
      rreId: this.projectDetail?.managerId,
      rreName: defaultRreName,
      collectionName: this.projectDetail?.collection || '',
      dueDate: this.formatDate(this.projectDetail?.dueDate || ''),
      contactPerson: this.projectDetail.contactPerson
    };

    // Auto-assign to logged-in user if project is unassigned
    if (this.isProjectUnassigned(this.projectDetail)) {
      payload.assigneeName = this.userDashboardService.userName;
      payload.assigneeId = this.userDashboardService.userId;
      payload.assigneeEmailId = this.userDashboardService.userEmail;
    }

    // If there are no documents, add noDocumentUploadsStatus with selected status (display text)
    const hasDocuments = this.projectDetail?.adminDocuments && this.projectDetail.adminDocuments.length > 0;
    if (!hasDocuments) {
      (payload as any).noDocumentUploadsStatus = activityTypeMap[this.selectedProjectStatus];
    }

    this.submittedProjectStatus = this.selectedProjectStatus;
    this.statusSubmittedForApproval = true;
    this.pendingStatusUpdate = !skipDialog;
    this.updateDeptSubTaskAssignee(payload);
    this.selectedProjectStatus = '';
    this.updateStatusNotes = '';
    this.isUpdateStatusExpanded = false;
  }

  emailDetails: any = {subject: '', email_body: '' };
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly userDashboardService: UserDashboardService,
    private readonly roleviewservice: RoleViewService,
    private readonly createProjectService: CreateProjectService,
    private readonly dashboardService: DashboardService,
    private readonly notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(queryParams => {
      this.fromManageApproval = queryParams.get('fromManageApproval') === 'true';
      // const fromManageApprovalParam = queryParams.get('fromManageApproval');
      // Default to the Manage Approval experience when the param is completely
      // absent (e.g. the RRE assignment email link, which only carries
      // departmentId and isn't generated by this app). Explicit 'false' -
      // used by the User Dashboard tab's plain project view - still opts out.
      // this.fromManageApproval = fromManageApprovalParam === null ? true : fromManageApprovalParam === 'true';
    });
    this.route.paramMap.subscribe(params => {
      const id = Number(params.get('id'));
      const userId = params.get('userId') || this.userId;
      // departmentId may arrive either as a matrix param (e.g. ;departmentId=CUST-46B4,
      // the format this app generates) or as a query param (e.g. ?departmentId=CUST-46B4,
      // used by some external links). Support both so the link works either way.
      const departmentId = params.get('departmentId') || this.route.snapshot.queryParamMap.get('departmentId') || '';
      this.projectId = id;
      if (departmentId) {
        this.departmentId = departmentId;
        this.fetchUserProjectDetails(id.toString(), departmentId, userId);
      } else {
        // If departmentId not in params, fetch without it
        this.fetchUserProjectDetails(id.toString(), '', userId);
      }
    });
  }
  projectDetail :any;
  apiProjectId = ''; // Store the actual project_id from API
  
  fetchUserProjectDetails(projectId:string, departmentId = '', _userId:string = this.userId) {
    this.isLoading = true;
    this.userDashboardService.fetchUserProjectDetails(projectId, departmentId).subscribe({
      next: (response: any) => {
        console.info("Project details fetched successfully", response);
        const mappedProject = this.mapApiResponseToProjectRecord(response.payload[0]);
        this.departmentId = mappedProject.departmentId;
        this.apiProjectId = response.payload[0]?.project_id || response.payload[0]?.projectId || response.payload[0]?.project_details_id || projectId;
        
        console.info("API Response payload:", response.payload[0]);
        console.info("Extracted apiProjectId:", this.apiProjectId, "departmentId:", this.departmentId);
        
        this.projectDetail = mappedProject;
        const rawPayload = response.payload[0];

        // If a Manage Approval link is opened (e.g. a copied/stale URL, or the email
        // link reused) after the request has already been approved by an RRE, tell
        // the user instead of silently showing a read-only page. Only check once,
        // on the initial load - not after this session's own approve/reject action,
        // which already has its own success dialog and redirect.
        if (this.fromManageApproval && !this.hasCheckedAlreadyApproved) {
          this.hasCheckedAlreadyApproved = true;
          if (this.isRreActionsDisabled(mappedProject)) {
            this.alreadyActionedType = this.isReworkState(mappedProject) ? 'rework' : 'approved';
            this.showAlreadyApprovedDialog = true;
            this.alreadyActionedOnOpen = true;
          }
        }

        // Detect rejection: API returns sub_task_status 'In Progress' with rre_id still present
        const isRework = (rawPayload?.sub_task_status || '').toLowerCase() === IN_PROGRESS && !!rawPayload?.rre_id;
        if (isRework) {
          // Reset document/status state so Step 2 is fully unlocked for re-confirmation
          this.statusSubmittedForApproval = false;
        }
        if (mappedProject.uploadedFilesData && mappedProject.uploadedFilesData.length > 0) {
          mappedProject.uploadedFilesData.forEach(file => {
            // Always reset pending status on REWORK; otherwise only initialise if not already tracked
            if (isRework || !this.documentStatusPending.hasOwnProperty(file.fileName)) {
              this.documentStatusPending[file.fileName] = '';
            }
          });
        }
        
        const project = this.selectedProject;
        this.fetchDeptTeamMembers(this.departmentId, projectId);
        this.projects = [mappedProject]
        console.info("Project details after fetch", project);
        this.selectedMemberId = this.selectedProject?.assignedToEmail ?? '';
        this.selectedMemberName = this.selectedProject?.assignedTo ?? '';
        this.selectedManagerName = this.selectedProject?.managerName ?? '';
        this.selectedManagerId = this.selectedProject?.managerEmailId ?? mappedProject.managerEmailId ?? '';

        // Once Task Assignment (Step 1) is completed, default the RRE Review (Step 3)
        // current owner/selection to that same person until an RRE reviewer is explicitly assigned.
        if (!this.selectedManagerId && this.selectedMemberId) {
          this.selectedManagerId = this.selectedMemberId;
          this.selectedManagerName = this.selectedMemberName;
        }
        this.showAssignmentPanel = false;
        this.showManagerAssignmentPanel = false;
        
        // Fetch activity stream data using the actual project_id from API
        console.info('Fetching activity stream with projectId:', this.apiProjectId, 'departmentId:', this.departmentId);
        this.fetchActivityStream(this.apiProjectId, this.departmentId);
        this.isLoading = false;

        // Auto-advance stepper to next incomplete step
        this.advanceToNextIncompleteStep();

        setTimeout(() => {
          if (this.pendingDocumentStatusSuccess) {
            this.pendingDocumentStatusSuccess = false;
            this.showDocumentStatusUpdatedSuccess(this.pendingDocumentStatusIsLast);
            this.pendingDocumentStatusIsLast = false;
          }
          if (this.pendingAssigneeSuccess) {
            this.pendingAssigneeSuccess = false;
            this.showAssigneeSuccess(this.isFirstAssignment, this.assignedMemberName);
            this.isFirstAssignment = false;
            this.assignedMemberName = '';
          }
          if (this.pendingDeleteSuccess) {
            this.pendingDeleteSuccess = false;
            this.showDeleteFileSuccess(this.deletedFileName);
            this.deletedFileName = '';
          }
        }, 1000);
      },
      error: (error: any) => {
        console.trace("Error fetching project details", error);
        this.isLoading = false;
      }
    });
  }

  private mapApiResponseToProjectRecord(apiResponse: any): ProjectRecord {
    // Detect rejection: API returns sub_task_status 'In Progress' with rre_id still present
    const isRework = (apiResponse.sub_task_status || '').toLowerCase() === IN_PROGRESS && !!apiResponse.rre_id;
    const projectStatus = this.mapStatusToProjectStatus(apiResponse.status || apiResponse.sub_task_status);
    
    // Extract full file data with versions (also used to get file names)
    const uploadedFilesData = this.extractUploadedFilesData(apiResponse.uploaded_files || []);

    // When rejected (REWORK), clear all document statuses so Step 2 resets for re-confirmation
    if (isRework) {
      uploadedFilesData.forEach((f: any) => { f.document_status = ''; });
    }
    
    // Derive file names from uploadedFilesData
    const uploadedFiles = uploadedFilesData.map(f => f.fileName);
    
    return {
      id: apiResponse.project_details_id,
      name: apiResponse.project_name,
      collection: apiResponse.collection_name,
      subCollections: Array.isArray(apiResponse.sub_collection_name) ? apiResponse.sub_collection_name : [],
      departmentId: apiResponse.department_id,
      departmentAssigned: apiResponse.department_name || '',
      contactPerson: apiResponse.contact_person || '',
      dueDate: apiResponse.due_date,
      documentNumber: apiResponse.document_number || '',
      status: projectStatus,
      assignedTo: apiResponse.assigned_person_name ? apiResponse.assigned_person_name : apiResponse.assigned_person_email_id,
      assignedToEmail: apiResponse.assigned_person_email_id,
      // Keep RRE assignment info from API even on REWORK so the reviewer name shows in Step 3
      managerId: apiResponse.rre_id || undefined,
      managerEmailId: apiResponse.rre_email_address || undefined,
      managerName: apiResponse.rre_name || undefined,
      subTaskStatus: apiResponse.sub_task_status || undefined,
      projectSubTaskId: apiResponse.project_sub_task_id || undefined,
      noDocumentUploadsStatus: isRework ? undefined : (apiResponse.no_doc_uploads_status || undefined),
      adminDocuments: uploadedFiles,
      uploadedDocuments: uploadedFiles,
      uploadedFilesData: uploadedFilesData,
      uploadedDocumentsByAdminDoc: {},
      documentStatusByAdminDoc: this.buildDocumentStatusMap(uploadedFilesData),
      subject: apiResponse.subject || '',
      email_body: apiResponse.email_body || ''
    };
  }

  private buildDocumentStatusMap(uploadedFilesData: any[]): Record<string, DocumentMarkStatus | ''> {
    const statusMap: Record<string, DocumentMarkStatus | ''> = {};
    uploadedFilesData.forEach(file => {
      const docStatus = file.document_status || '';
      // Map API status values to DocumentMarkStatus
      if (docStatus === NO_CHANGE_REQUIRED || docStatus === NOT_RESPONSIBLE || docStatus === COMPLETED) {
        statusMap[file.fileName] = docStatus as DocumentMarkStatus;
      } else {
        statusMap[file.fileName] = '';
      }
    });
    return statusMap;
  }

  extractUploadedFilesData(uploadedFiles: any[]): any[] {
  const groupedByRaesFileId = new Map<string, any>();

  uploadedFiles.sort((a: any, b: any) => {
    const versionA = Number(a.version_number || 1);
    const versionB = Number(b.version_number || 1);
    return versionA - versionB;
  });

  uploadedFiles.forEach((file: any, index: number) => {
    const fileName = file.file_name || `File-${index + 1}`;
    const key = String(file.raes_uploaded_file_id || `FILE-${index + 1}`);

    if (groupedByRaesFileId.has(key)) {
      // Update document_status if a newer version has it
      const existingGroup = groupedByRaesFileId.get(key);
      if (file.document_status && file.document_status !== 'Not started') {
        existingGroup.document_status = file.document_status;
      }
    } else {
      groupedByRaesFileId.set(key, {
        fileId: String(file.file_versioning_id || file.document_id || `FILE-${index + 1}`),
        fileName: fileName,
        file_versioning_id: String(file.file_versioning_id || file.document_id || ''),
        file_name: fileName,
        document_status: file.document_status || '',
        raes_uploaded_file_id: file.raes_uploaded_file_id,
        versions: []
      });
    }

    const fileGroup = groupedByRaesFileId.get(key);

    fileGroup.versions.push({
      versionNumber: String(file.version_number || '1'),
      uploadedBy: file.file_uploaded_by || 'Unknown',
      fileName: file.file_name || 'Unknown',
      uploadDate: file.file_uploaded_at ? this.formatDate(file.file_uploaded_at) : '',
      uploadTime: file.file_uploaded_at ? this.formatTime(file.file_uploaded_at) : '',
      isLatest: false,
      fileVersioningId: String(file.file_versioning_id || ''),
      file_versioning_id: String(file.file_versioning_id || ''),
      file_name: file.file_name || 'Unknown'
    });
  });

  return Array.from(groupedByRaesFileId.values()).map(fileGroup => {
    fileGroup.versions.sort(
      (a: any, b: any) => Number(b.versionNumber) - Number(a.versionNumber)
    );

    if (fileGroup.versions.length > 0) {
      fileGroup.versions[0].isLatest = true;
    }

    return fileGroup;
  });
}

  formatDate(dateString: string): string {
    if (!dateString) {
      return '';
    }
    const datePart = dateString.split('T')[0].split(' ')[0];
    const parts = datePart.includes('-') ? datePart.split('-').map(Number) : null;
    if (parts?.length !== 3) {
      return dateString;
    }
    const [year, month, day] = parts;
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
      return dateString;
    }
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[month - 1]} ${String(day).padStart(2, '0')}, ${year}`;
  }

  formatTime(dateString: string): string {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  }

  toggleFileExpand(fileId: string): void {
    this.expandedFileId = this.expandedFileId === fileId ? null : fileId;
  }

  triggerFileUpload(fileId: string, uploadType: 'reupload' | 'new-version'): void {
    this.currentFileUploadId = fileId;
    this.currentFileUploadType = uploadType;
    this.documentFileInput.nativeElement.click();
  }

  handleDocumentFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0 && this.currentFileUploadId) {
      const file = this.projectDetail;
      const uploadedFile = this.projectDetail?.uploadedFilesData?.find(
        (f: any) => f.fileId === this.currentFileUploadId
      );
      console.info("Uploaded file found:", uploadedFile);
      if (uploadedFile) {
        // Pass file_versioning_id so backend creates a new version under the same document
        const versioningId = uploadedFile.file_versioning_id || this.currentFileUploadId;
        this.onUploadFinalDocument(event, file, uploadedFile.fileName, uploadedFile.raes_uploaded_file_id, versioningId, this.currentFileUploadType);
      }
      
      // Reset file input
      input.value = '';
      this.currentFileUploadId = null;
      this.currentFileUploadType = '';
    }
  }

  private mapStatusToProjectStatus(apiStatus: string): ProjectStatus {
    const statusMap: { [key: string]: ProjectStatus } = {
      'submitted': 'Submitted',
      'assigned': 'Assigned',
      'in progress': IN_PROGRESS_STATUS,
      'pending rre review': PENDING_RRE_REVIEW,
      'pending raes review': PENDING_RAES_REVIEW,
      'completed': 'Completed',
      'unassigned': 'Unassigned'
    };
    
    // Convert to lowercase for case-insensitive matching
    const normalizedStatus = apiStatus?.toLowerCase() || 'unassigned';
    return statusMap[normalizedStatus] || 'Unassigned';
  }

  fetchDeptTeamMembers(departmentId: string, projectId: string) {
    this.userDashboardService.fetchSelectedDeptTeamMembers(departmentId, projectId).subscribe({
      next: (response: any) => {
        console.info("Selected department team members fetched successfully", response);
        const mappedDepartment = this.mapApiResponseToTeamMember(response.payload);

        this.departmentMembers = mappedDepartment;
        this.originalDepartmentMembers =mappedDepartment;
      },
      error: (error: any) => {
        console.trace("Error fetching selected department team members", error);
      }
    });
  }
  private mapApiResponseToTeamMember(apiResponse: any): TeamMember[] {
    return apiResponse.map((member: any) => ({
      id: member.user_id,
      name: member.tm_name,
      jobCode: member.job_code,
      jobTitle: member.job_title,
      email: member.email_address
    }));
  }

  updateDeptSubTaskAssignee(request: any) {
    this.userDashboardService.updateDeptSubTaskAssignee(request).subscribe({
      next: (response: any) => {
        console.info("Department sub-task assignee updated successfully", response);
        if ((request.status === 'Assigned' || request.status === IN_PROGRESS_STATUS) && !this.pendingStatusUpdate) {
          const wasUnassigned = !this.projectDetail?.assignedTo || this.projectDetail?.assignedTo === 'Unassigned';
          this.isFirstAssignment = wasUnassigned;
          this.assignedMemberName = request.assigneeName || '';
          this.pendingAssigneeSuccess = true;
          
          this.showAssignmentPanel = false;
          this.assignmentMode = '';
          this.memberSearchTerm = '';
        }
        // Optimistically update subTaskStatus so the template reacts immediately
        // (before the fetchUserProjectDetails round-trip completes)
        if (request.status === PENDING_RRE_REVIEW || request.status === PENDING_RAES_REVIEW) {
          if (this.projectDetail) {
            this.projectDetail.subTaskStatus = request.status;
            this.projectDetail.status = request.status;
          }
        }
        if (this.pendingStatusUpdate) {
          this.pendingStatusUpdate = false;
          this.showTaskStatusSuccess();
        }
        this.fetchUserProjectDetails(request.projectId.toString(), this.projectDetail?.departmentId || '', this.userId);
      },
      error: (error: any) => {
        console.trace("Error updating department sub-task assignee", error);
        if (this.pendingStatusUpdate) {
          this.pendingStatusUpdate = false;
        }
      }
    });
  }

  
  private autoAssignToCurrentUser(project: ProjectRecord, statusForAssignment: string = IN_PROGRESS_STATUS): void {
    const currentUserEmail = this.userDashboardService.userEmail;
    const currentUserName = this.userDashboardService.userName;
    const currentUserId = this.userDashboardService.userId;

    const autoAssignPayload = {
      projectId: this.projectId,
      departmentId: project.departmentId,
      updatedBy: currentUserName,
      assigneeName: currentUserName,
      assigneeId: currentUserId,
      status: statusForAssignment,
      projectSubTaskId: project.projectSubTaskId,
      assigneeEmailId: currentUserEmail,
      userComments: '',
      activityType: 'Auto-Assigned',
      departmentName: this.projectDetail?.departmentName || '',
      projectName: this.projectDetail?.name || '',
      collectionName: this.projectDetail?.collection || '',
      dueDate: this.formatDate(this.projectDetail?.dueDate || ''),
      contactPerson: this.projectDetail.contactPerson,
      activityDescription: `Task automatically assigned to ${currentUserName} upon taking first action.`
    };

    this.updateDeptSubTaskAssignee(autoAssignPayload);
  }

  private isProjectUnassigned(project: ProjectRecord | undefined): boolean {
    if (!project) {
      return false;
    }
    return !project.assignedTo || project.assignedTo === 'Unassigned';
  }

  selectedMemberName = '';
  memberSearchTerm = '';
  managerSearchTerm = '';

  onChangeAssigneeSelectionChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;
    console.info("Selected member ID changed to:", selectedValue);
    this.selectedMemberId = selectedValue;
   
  }

  toggleAssignmentPanel(forceOpen = false): void {
    this.showAssignmentPanel = forceOpen ? true : !this.showAssignmentPanel;
    if (!this.showAssignmentPanel) {
      this.resetAssignmentSelection();
    }
  }

  hasAssignedMember(project: ProjectRecord | undefined): boolean {
    if (!project) {
      return false;
    }

    return !!(project.assignedToEmail || project.assignedTo);
  }

  selectMember(member: TeamMember): void {
    this.selectedMemberId = member.email;
    this.selectedMemberName = member.name;
  }

  onMemberSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.memberSearchTerm = input.value.toLowerCase();
  }

  getFilteredMembers(): TeamMember[] {
    if (!this.memberSearchTerm) {
      return this.departmentMembers;
    }
    return this.departmentMembers.filter(member =>
      member.name.toLowerCase().includes(this.memberSearchTerm) ||
      member.email.toLowerCase().includes(this.memberSearchTerm) ||
      member.jobTitle.toLowerCase().includes(this.memberSearchTerm)
    );
  }

  getSelectedMemberName(): string {
    if (!this.selectedMemberId) {
      return '';
    }
    const member = this.departmentMembers.find(m => m.email === this.selectedMemberId);
    return member ? member.name : '';
  }

  private resetAssignmentSelection(): void {
    this.assignmentMode = 'self';
    this.selectedMemberId = '';
    this.memberSearchTerm = '';
  }

  selectManager(member: TeamMember): void {
    this.selectedManagerId = member.email;
  }


async onManagerSearch(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;

  clearTimeout(this.debounceTimer);

  this.debounceTimer = setTimeout(async () => {
    const value = input.value.trim();

    if (value === '') {
      this.departmentMembers = [...this.originalDepartmentMembers];
      this.managerSearchTerm = '';
      return;
    }

    const searchData = await this.userDashboardService.searchAllUsersDetails(value);
    this.departmentMembers = [
      ...this.originalDepartmentMembers,
      ...this.mapSearchAllUserData(searchData?.payload)
    ];

    this.managerSearchTerm = value.toLowerCase();
  }, 1000);
}

  mapSearchAllUserData(searchData: any[]): any[] {
    return searchData.map((member: any) => ({
      name: member.tm_name,
      jobTitle: member.job_title,
      email: member.email_address
    }));
  }

  

  getFilteredManagers(): TeamMember[] {
    if (!this.managerSearchTerm) {
      return this.departmentMembers;
    }
    return this.departmentMembers.filter(member =>
      member.name.toLowerCase().includes(this.managerSearchTerm) ||
      member.email.toLowerCase().includes(this.managerSearchTerm) ||
      member.jobTitle.toLowerCase().includes(this.managerSearchTerm)
    );
  }

  getSelectedManagerName(): string {
    if (!this.selectedManagerId) {
      return '';
    }
    const manager = this.departmentMembers.find(m => m.email === this.selectedManagerId);
    return manager ? manager.name : (this.selectedManagerName || '');
  }

  hasAssignedManager(_project: ProjectRecord | undefined): boolean {
    return !!this.selectedManagerId;
  }

  toggleManagerAssignmentPanel(forceOpen = false): void {
    this.showManagerAssignmentPanel = forceOpen ? true : !this.showManagerAssignmentPanel;
    if (!this.showManagerAssignmentPanel) {
      this.managerAssignmentMode = '';
      this.managerSearchTerm = '';
    }
  }

  assignManagerToMember(isSelf = false): void {
    const selectedManager = this.departmentMembers.find(member => member.email === this.selectedManagerId);
    if (isSelf || selectedManager) {
      this.selectedManagerId = isSelf ? this.userDashboardService.userEmail : (selectedManager?.email ?? this.selectedManagerId);
      this.showManagerAssignmentPanel = false;
      this.managerAssignmentMode = '';
      this.managerSearchTerm = '';
      const payload = {
        projectId: this.projectId,
        projectName: this.projectDetail.name,
        departmentName: this.projectDetail.departmentAssigned,
        departmentId: this.projectDetail.departmentId,
        updatedBy: this.userDashboardService.userName,
        assigneeName: this.projectDetail?.assignedTo || this.selectedMemberName,
        assigneeId: this.projectDetail?.assignedToEmail || this.selectedMemberId,
        assigneeEmailId: this.projectDetail?.assignedToEmail || this.selectedMemberId,
        status: PENDING_RRE_REVIEW,
        projectSubTaskId: this.projectDetail.projectSubTaskId,
        userComments: '',
        activityType: RRE_REVIEW,
        collectionName: this.projectDetail?.collection ,
        dueDate: this.formatDate(this.projectDetail?.dueDate),
        activityDescription: `RRE review task assigned to ${isSelf ? this.userDashboardService.userName : selectedManager?.name} (${isSelf ? this.userDashboardService.userEmail : selectedManager?.email}).`,
        rreEmailAddress: isSelf ? this.userDashboardService.userEmail : selectedManager?.email,
        rreId: isSelf ? this.userDashboardService.userUuid : selectedManager?.id,
        rreName: isSelf ? this.userDashboardService.userName : selectedManager?.name,
      contactPerson: this.projectDetail.contactPerson,
        noDocumentUploadsStatus: this.projectDetail?.noDocumentUploadsStatus ?? null
      };
      this.updateDeptSubTaskAssignee(payload);
    }
  }

  private resetManagerAssignmentSelection(): void {
    this.managerAssignmentMode = 'others';
    this.selectedManagerId = '';
    this.managerSearchTerm = '';
  }

  get selectedProject(): ProjectRecord | undefined {
    return this.projects.find(project => project.id === this.projectId);
  }

  // get availableManagers(): TeamMember[] {
  //   return this.departmentMembers.filter(member => member.role === 'Manager');
  // }

  get canSendForApproval(): boolean {
    const project = this.selectedProject;
    if (!project) {
      return false;
    }

    return !!project.assignedTo && !!this.selectedManagerId && this.hasUploadedDocuments(project);
  }

  goBack(): void {
    if (this.fromManageApproval) {
      this.router.navigate(['/user-dashboard/manage-approval']);
    } else {
      this.goToDashboard();
    }
  }

  goToDashboard(): void {
    this.router.navigate(['/user-dashboard']);
  }

  setAssignmentMode(mode: 'self' | 'others'): void {
    this.assignmentMode = mode;
    if (mode === 'self') {
      this.selectedMemberId = this.userId;
    } else if (this.selectedMemberId === this.userId) {
      this.selectedMemberId = '';
    }
  }

  setManagerAssignmentMode(mode: 'self' | 'others'): void {
    this.managerAssignmentMode = mode;
    if (mode === 'self') {
      this.selectedManagerId = this.userId;
    } else if (this.selectedManagerId === this.userId) {
      this.selectedManagerId = '';
    }
  }


  assignToSelf(project: ProjectRecord): void {
    project.assignedTo = this.userId;
    this.selectedMemberId = this.userId;
    this.assignmentMode = 'self';
    if (project.status === 'Unassigned') {
      project.status = 'Assigned';
    }
  }

  assignToMember(isSelf = false): void {
    
     const selectedMember = this.departmentMembers.find(member => member.email === this.selectedMemberId);
      const payload = {
      projectId: this.projectId,
      projectName: this.projectDetail.name,
      departmentName: this.projectDetail.departmentAssigned,
      departmentId: this.projectDetail.departmentId,
      updatedBy: this.userDashboardService.userName,
      assigneeName: isSelf ? this.userDashboardService.userName  : selectedMember?.name,
      assigneeId: isSelf ? this.userDashboardService.userUuid  :  selectedMember?.id,
      status: IN_PROGRESS_STATUS,
      projectSubTaskId: this.projectDetail.projectSubTaskId,
      assigneeEmailId: isSelf ? this.userDashboardService.userEmail  :  selectedMember?.email,
      userComments: '',
      activityType: 'Assigned',
      activityDescription: `Task assigned to ${isSelf ? this.userDashboardService.userName  : selectedMember?.name} (${isSelf ? this.userDashboardService.userEmail  :  selectedMember?.email}).`,
      rreEmailAddress: this.projectDetail?.managerEmailId,
      rreId: this.projectDetail?.managerId,
      rreName: this.projectDetail?.managerName,
      collectionName: this.projectDetail?.collection || '',
      dueDate: this.formatDate(this.projectDetail?.dueDate || ''),
      contactPerson: this.projectDetail?.contactPerson || '',
      noDocumentUploadsStatus: this.projectDetail?.noDocumentUploadsStatus ?? null
    };
    this.updateDeptSubTaskAssignee(payload);
    // }
  }
  assignToMemberO(project: ProjectRecord): void {
    if (!this.selectedMemberId) {
      return;
    }

    project.assignedTo = this.selectedMemberName ? this.selectedMemberName : this.selectedMemberId;
    this.assignmentMode = this.selectedMemberId === this.userId ? 'self' : 'others';
    if (project.status === 'Unassigned') {
      project.status = 'Assigned';
    }
   
  }

  onUploadFinalDocument(event: Event, project: ProjectRecord, adminDocumentName: string, raesUploadedFileId:number, documentId?: string, uploadType?: 'reupload' | 'new-version' | ''): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const workDayId = this.roleviewservice.getWorkdayId();
    const userName = this.roleviewservice.getUserName();

    if (!workDayId || !userName) {
      console.trace('Missing user information for upload:', { workDayId, userName });
      return;
    }

    for (let i = 0; i < input.files.length; i++) {
      const f:any = input.files.item(i);
      if (!this.isAllowedFileType(f)) {
        console.info('Unsupported file type blocked:', f.name, f.type);
        continue;
      }

      this.fileToBase64(f).then(base64 => {
        // Find existing file_versioning_id by fileName to ensure version increment
        const existingVersioningId = this.findExistingDocumentVersioningId(f.name);
       
        const uploadPayload: any = {
          projectId: String(project.id),
          projectName: project.name,
          fileName: f.name,
          contentType: f.type,
          userName: userName,
          fileBuffer: base64,
          file_versioning_id: existingVersioningId || documentId,
          uploadType: existingVersioningId ? 'new-version' : (uploadType || 'reupload'),
          departmentIds: project.departmentId ? [project.departmentId] : [],
          raesUploadedFileId: raesUploadedFileId
        };
        this.createProjectService.uploadDocumentUser(workDayId, uploadPayload).subscribe({
          next: (res) => {
            console.info("Document uploaded successfully", res);
            
            if (this.isProjectUnassigned(project)) {
              this.autoAssignToCurrentUser(project, IN_PROGRESS_STATUS);
            }
            
            this.ensureDocumentUploadMap(project);
            const targetFiles = project.uploadedDocumentsByAdminDoc?.[adminDocumentName] ?? [];
            if (!targetFiles.includes(f.name)) {
              targetFiles.push(f.name);
            }
            if (!project.uploadedDocuments.includes(f.name)) {
              project.uploadedDocuments.push(f.name);
            }
            if (project.uploadedDocumentsByAdminDoc) {
              project.uploadedDocumentsByAdminDoc[adminDocumentName] = targetFiles;
            }
            if (project.status === 'Assigned') {
              project.status = IN_PROGRESS_STATUS;
            }
            // Fetch updated project details after upload
            this.fetchUserProjectDetails(String(project.id), this.departmentId, this.userId);
            
            // Refresh activity stream after a short delay to ensure backend has registered the activity
            const uploadedFileName = f.name;
            setTimeout(() => {
              this.fetchActivityStream(this.apiProjectId || String(project.id), this.departmentId);
              setTimeout(() => {
                this.showDocumentUploadSuccess(uploadedFileName);
              }, 500);
            }, 1000);
          },
          error: (error) => {
            console.trace("Document upload failed", error);
          }
        });
      });
    }

    try { input.value = ''; } catch (e) { console.trace('Error clearing file input:', e); }
  }

  private isAllowedFileType(file: File): boolean {
    const allowedMimes = new Set<string>([
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel.sheet.macroenabled.12',
      // 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/csv',
      'image/png',
      'image/jpeg'
    ]);

    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();

    if (type && allowedMimes.has(type)) {
      return true;
    }

    return (
      name.endsWith('.pdf') ||
      name.endsWith('.docx') ||
      name.endsWith('.xlsx') ||
      name.endsWith('.xlsm') ||
      name.endsWith('.png') ||
      name.endsWith('.jpg') ||
      name.endsWith('.jpeg')|| 
      // name.endsWith('.pptx') || 
      name.endsWith('.csv')
    );
  }

  
  private findExistingDocumentVersioningId(fileName: string): string | null {
    if (!this.projectDetail?.uploadedFilesData?.length) {
      return null;
    }

    const existingFile = this.projectDetail.uploadedFilesData.find((file: UploadedFileData) => {
      const existingFileName = (file.fileName || file.file_name || '').toLowerCase();
      return existingFileName === fileName.toLowerCase();
    });

    return existingFile?.file_versioning_id || null;
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  }

  openFilePicker(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  sendForManagerApproval(project: ProjectRecord): void {
    if (!this.canSendForApproval) {
      return;
    }

    project.managerId = this.selectedManagerId;
    project.status = PENDING_RRE_REVIEW;
  }

  assignManagerToSelf(project: ProjectRecord): void {
    if (!this.isManagerTaskEnabled(project) || !project.assignedTo) {
      return;
    }

    this.selectedManagerId = this.userId;
    this.sendForManagerApproval(project);
  }

  assignManagerToOthers(project: ProjectRecord): void {
    if (!this.isManagerTaskEnabled(project)) {
      return;
    }

    this.sendForManagerApproval(project);
  }

  viewAdminDocument(project: ProjectRecord, documentName: string): void {
    console.info(`Viewing admin document for project ${project.id}: ${documentName}`);
  }

  downloadAdminDocument(project: ProjectRecord, documentName: string): void {
    console.info(`Downloading admin document for project ${project.id}: ${documentName}`);
    // Find the file from uploadedFilesData
    if (project.uploadedFilesData && project.uploadedFilesData.length > 0) {
      const fileFound = project.uploadedFilesData.find(f => f.fileName === documentName);
      if (fileFound) {
        this.download(fileFound);
      }
    }
  }

  viewUploadedDocument(project: ProjectRecord, adminDocumentName: string, uploadedDocumentName: string): void {
    console.info(
      `Viewing uploaded document for project ${project.id}, admin document ${adminDocumentName}: ${uploadedDocumentName}`
    );
  }

  downloadUploadedDocument(project: ProjectRecord, adminDocumentName: string, uploadedDocumentName: string): void {
    console.info(
      `Downloading uploaded document for project ${project.id}, admin document ${adminDocumentName}: ${uploadedDocumentName}`
    );
    if (project.uploadedFilesData && project.uploadedFilesData.length > 0) {
      const fileFound = project.uploadedFilesData.find(f => f.fileName === uploadedDocumentName);
      if (fileFound) {
        this.download(fileFound);
      }
    }
  }

  
  download(file: any): void {
    const id = file.file_versioning_id;
    if (id) {
      const workDayId = this.roleviewservice.getWorkdayId();
      if (!workDayId) {
        console.trace('WorkDayId not available for download');
        return;
      }
      this.createProjectService.readDocument(workDayId, id).subscribe((res: any) => {
        console.info("**res", res);
        if (!res?.body) {
          console.trace('No body returned from API');
          return;
        }
        try {
          const base64Data = res.body.includes(',') ? res.body.split(',')[1] : res.body;
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.codePointAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/octet-stream' });
          const url = globalThis.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.file_name || 'download';
          document.body.appendChild(a);
          a.click();
          a.remove();
          globalThis.URL.revokeObjectURL(url);
        } catch (error) {
          console.trace('Download failed', error);
        }
      });
      return;
    }
    console.info('No file_versioning_id found', file);
  }

  canDeleteFile(version: any): boolean {
    if (!version?.uploadedBy) {
      return false;
    }
     if (version.versionNumber === '1' || version.versionNumber === 1) {
      return false;
    }
    const currentUserName = this.userDashboardService.userName;
    return version.uploadedBy === currentUserName;
  }

  deleteFile(fileVersioningId: string, fileName: string): void {
    if (!fileVersioningId) {
      console.trace('Invalid file versioning ID for deletion');
      return;
    }

    this.deleteFileDetails = { fileVersioningId, fileName };
    this.showDeleteFileDialog = true;
  }

  confirmDeleteFile(): void {
    const { fileVersioningId, fileName } = this.deleteFileDetails;

    if (!fileVersioningId) {
      console.trace('Invalid file versioning ID for deletion');
      return;
    }

    const workDayId = this.roleviewservice.getWorkdayId();
    if (!workDayId) {
      console.trace('WorkDayId not available for deletion');
      return;
    }

    this.createProjectService.deleteDocument( workDayId, fileVersioningId).subscribe({
      next: (res) => {
        console.info("Document deleted successfully", res);
        this.closeDeleteFileDialog();
        
        // Set pending flag and file name to show success after loader completes
        this.pendingDeleteSuccess = true;
        this.deletedFileName = fileName;
        
        // Refresh project details after deletion
        this.fetchUserProjectDetails(String(this.projectId), this.departmentId, this.userId);
        
        // Refresh activity stream after a short delay
        setTimeout(() => {
          this.fetchActivityStream(this.apiProjectId || String(this.projectId), this.departmentId);
        }, 1000);
      },
      error: (error) => {
        console.trace("Document deletion failed", error);
        this.closeDeleteFileDialog();
        alert('Failed to delete the file. Please try again.');
      }
    });
  }

  showDeleteFileSuccess(fileName: string): void {
    this.successDialogTitle = 'Document Deleted Successfully';
    this.successDialogMessage = `<strong>${fileName}</strong> has been deleted successfully.`;
    this.showNextStep = false;
    this.showStatusSuccessDialog = true;
  }

  closeDeleteFileDialog(): void {
    this.showDeleteFileDialog = false;
    this.deleteFileDetails = { fileVersioningId: '', fileName: '' };
  }

  deleteUploadedDocument(project: ProjectRecord, adminDocumentName: string, uploadedDocumentName: string): void {
    this.ensureDocumentUploadMap(project);

    const sectionDocuments = project.uploadedDocumentsByAdminDoc?.[adminDocumentName] ?? [];
    const sectionIndex = sectionDocuments.indexOf(uploadedDocumentName);
    if (sectionIndex > -1) {
      sectionDocuments.splice(sectionIndex, 1);
    }

    const overallIndex = project.uploadedDocuments.indexOf(uploadedDocumentName);
    if (overallIndex > -1) {
      project.uploadedDocuments.splice(overallIndex, 1);
    }
  }

  getUploadedDocumentsForAdminDocument(project: ProjectRecord, adminDocumentName: string): string[] {
    this.ensureDocumentUploadMap(project);
    return project.uploadedDocumentsByAdminDoc?.[adminDocumentName] ?? [];
  }

  setDocumentMarkStatus(project: ProjectRecord, adminDocumentName: string, status: DocumentMarkStatus): void {
    this.ensureDocumentStatusMap(project);
    if (project.documentStatusByAdminDoc) {
      project.documentStatusByAdminDoc[adminDocumentName] = status;
    }
  }

  confirmDocumentStatus(project: ProjectRecord, fileName: string): void {
    const status = this.documentStatusPending[fileName];
    if (!status) {
      return;
    }
    
    // Call the API to update document status
    this.updateDocumentStatus(project, fileName, status);
  }

  updateDocumentStatus(project: ProjectRecord, fileName: string, status: DocumentMarkStatus): void {
    const fileData = project.uploadedFilesData?.find(f => f.fileName === fileName);
    const payload = {
      projectId: this.projectId,
      departmentId: this.departmentId || project.departmentId,
      fileName: fileName,
      documentStatus: status,
      userComments: '',
      raesUploadedFileId: fileData?.raes_uploaded_file_id
    };

    this.userDashboardService.updateDocumentStatus(payload).subscribe({
      next: (response: any) => {
        console.info('Document status updated successfully:', response);
        // Update local state after successful API call
        this.setDocumentMarkStatus(project, fileName, status);
        this.documentStatusPending[fileName] = '';
        this.documentStatusEditing[fileName] = false;
        
        // Also update the document_status in uploadedFilesData
        const fileData = project.uploadedFilesData?.find(f => f.fileName === fileName);
        if (fileData) {
          fileData.document_status = status;
        }
        
        const isLastDocument = this.allDocumentsHaveStatus(project);
        // Rework state: sub_task_status = 'In Progress' and an RRE was previously assigned
        const isReworkState = (project.subTaskStatus || '').toLowerCase() === IN_PROGRESS && !!project.managerId;
        if (this.isProjectUnassigned(project)) {
          // If last document → Pending RRE Review, otherwise → In Progress
          const statusForAssignment = isLastDocument ? PENDING_RRE_REVIEW : IN_PROGRESS_STATUS;
          this.autoAssignToCurrentUser(project, statusForAssignment);
        } else if (isLastDocument && isReworkState) {
          // Rework re-submission: all docs re-confirmed — resubmit to the same RRE
          // without requiring the user to manually re-assign in step 3
          const reworkResubmitPayload = {
            projectId: this.projectId,
            departmentId: project.departmentId || this.departmentId,
            updatedBy: this.userDashboardService.userName,
            assigneeName: project.assignedTo || this.userDashboardService.userName,
            assigneeId: project.assignedToEmail || this.userDashboardService.userId,
            assigneeEmailId: project.assignedToEmail || this.userDashboardService.userEmail,
            status: PENDING_RRE_REVIEW,
            projectSubTaskId: project.projectSubTaskId,
            userComments: '',
            activityType: 'Resubmitted',
            departmentName: this.projectDetail?.departmentAssigned || '',
            projectName: this.projectDetail?.name || '',
            collectionName: this.projectDetail?.collection || '',
            dueDate: this.formatDate(this.projectDetail?.dueDate || ''),
            activityDescription: 'Documents re-confirmed after rework. Resubmitted for RRE review.',
            rreEmailAddress: project.managerEmailId,
            rreId: project.managerId,
            contactPerson: this.projectDetail.contactPerson,
            rreName: project.managerName,
          };
          this.updateDeptSubTaskAssignee(reworkResubmitPayload);
        } else if (isLastDocument) {
          // First-time flow: project was already assigned in Step 1 and this isn't a rework.
          // Persist the transition to "Pending RRE Review" now so the real sub_task_status
          // matches the Step 3 "current owner" default shown here (and stays consistent with
          // the dashboard/other screens), defaulting the RRE reviewer to the Step 1 assignee
          // until someone explicitly picks a different reviewer.
          const defaultRreEmail = this.selectedManagerId || project.assignedToEmail || '';
          const defaultRreName = this.selectedManagerName || project.assignedTo || '';
          const autoRreReviewPayload = {
            projectId: this.projectId,
            departmentId: project.departmentId || this.departmentId,
            updatedBy: this.userDashboardService.userName,
            assigneeName: project.assignedTo || this.userDashboardService.userName,
            assigneeId: project.assignedToEmail || this.userDashboardService.userId,
            assigneeEmailId: project.assignedToEmail || this.userDashboardService.userEmail,
            status: PENDING_RRE_REVIEW,
            projectSubTaskId: project.projectSubTaskId,
            userComments: '',
            activityType: RRE_REVIEW,
            departmentName: this.projectDetail?.departmentAssigned || '',
            projectName: this.projectDetail?.name || '',
            collectionName: this.projectDetail?.collection || '',
            dueDate: this.formatDate(this.projectDetail?.dueDate || ''),
            activityDescription: `All documents reviewed. Project moved to Pending RRE Review (RRE: ${defaultRreName}).`,
            rreEmailAddress: defaultRreEmail,
            rreId: project.managerId,
            contactPerson: this.projectDetail.contactPerson,
            rreName: defaultRreName,
            noDocumentUploadsStatus: project.noDocumentUploadsStatus ?? null
          };
          this.updateDeptSubTaskAssignee(autoRreReviewPayload);
        }
      
       
        setTimeout(() => {
          this.fetchActivityStream(this.apiProjectId || String(this.projectId), this.departmentId);
        }, 1000);
        
        
        this.showDocumentStatusUpdatedSuccess(isLastDocument);
      },
      error: (error: any) => {
        console.trace('Error updating document status:', error);
      }
    });
  }

  getDocumentMarkStatus(project: ProjectRecord, adminDocumentName: string): DocumentMarkStatus | '' {
    this.ensureDocumentStatusMap(project);
    return project.documentStatusByAdminDoc?.[adminDocumentName] ?? '';
  }

  areAllDocumentsMarked(project: ProjectRecord): boolean {
    this.ensureDocumentStatusMap(project);
    if (!project.adminDocuments || project.adminDocuments.length === 0) {
      return false; 
    }
    
    return project.adminDocuments.every(docName => {
      const status = project.documentStatusByAdminDoc?.[docName];
      return status !== undefined && status !== '';
    });
  }

  canProceedToAssignment(project: ProjectRecord): boolean {
    if (this.isStepCompleted(2)) {
      return true;
    }
    if (project.adminDocuments && project.adminDocuments.length > 0) {
      return this.areAllDocumentsMarked(project);
    }
    return this.statusSubmittedForApproval || !!project.noDocumentUploadsStatus;
  }

  // === Stepper Logic ===

  isStepCompleted(step: number): boolean {
    switch (step) {
      case 1:
        return !!this.selectedMemberName;
      case 2:
        // In manage approval view, documents were already actioned by the submitter before RRE review.
        // Check both project.status and subTaskStatus because after a rework re-submission
        // project.status may still be 'In Progress' while subTaskStatus is 'Pending RRE Review'.
        if (this.fromManageApproval && (
          this.projectDetail?.status === PENDING_RRE_REVIEW ||
          this.projectDetail?.status === PENDING_RAES_REVIEW ||
          this.projectDetail?.status === 'Completed' ||
          this.projectDetail?.subTaskStatus === PENDING_RRE_REVIEW ||
          this.projectDetail?.subTaskStatus === 'APPROVED' ||
          this.projectDetail?.subTaskStatus === PENDING_RAES_REVIEW)) {
          return true;
        }
        if (this.projectDetail?.uploadedFilesData?.length > 0) {
          return this.areAllDocumentsMarked(this.projectDetail);
        }
        // When subTaskStatus is Completed, preserve the document status as-is from the API
        // instead of resetting to an empty dropdown
        return this.statusSubmittedForApproval
          || !!this.projectDetail?.noDocumentUploadsStatus
          || this.projectDetail?.subTaskStatus === 'Completed';
      case 3:
        return this.projectDetail?.status === PENDING_RAES_REVIEW ||
          this.projectDetail?.subTaskStatus === PENDING_RAES_REVIEW ||
          this.projectDetail?.status === 'Completed' ||
          this.projectDetail?.subTaskStatus === 'Completed';
      case 4:
        return this.projectDetail?.status === 'Completed' ||
          this.projectDetail?.subTaskStatus === 'Completed';
      default:
        return false;
    }
  }

  isStepAccessible(step: number): boolean {
    if (step === 1) {
      return true;
    }
    if (step === 4) {
      return this.isStepCompleted(3) &&
        (this.projectDetail?.subTaskStatus === 'APPROVED' ||
         this.projectDetail?.subTaskStatus === PENDING_RAES_REVIEW ||
         this.projectDetail?.status === PENDING_RAES_REVIEW ||
         this.projectDetail?.status === 'Completed');
    }
    return this.isStepCompleted(step - 1);
  }

  areAllStepsCompleted(): boolean {
    const maxSteps = this.fromManageApproval ? 2 : 4;
    for (let i = 1; i <= maxSteps; i++) {
      if (!this.isStepCompleted(i)) {
        return false;
      }
    }
    return maxSteps > 0;
  }
   onStepperStepClick(step: number): void {
    if (this.isStepAccessible(step) || this.isStepCompleted(step)) {
      this.activeStepperStep = this.activeStepperStep === step ? 0 : step;
    }
  }

  advanceToNextIncompleteStep(): void {
    const maxSteps = this.fromManageApproval ? 2 : 4;
    for (let i = 1; i <= maxSteps; i++) {
      if (!this.isStepCompleted(i)) { this.activeStepperStep = i; return; }
    }
    this.activeStepperStep = 0;
  }

  isManagerTaskEnabled(project: ProjectRecord): boolean {
    return this.hasUploadedDocuments(project);
  }

  getTopStatusLabel(project: ProjectRecord): string {
    // While the user is on Step 3 (RRE Review) and a current owner is already shown
    // (defaulted or explicitly assigned), reflect that as "Pending RRE Review" even
    // if the persisted subTaskStatus hasn't been updated yet.
    if (this.activeStepperStep === 3 && !this.isStepCompleted(3) && !!this.getSelectedManagerName()) {
      return PENDING_RRE_REVIEW;
    }
    return project.subTaskStatus || '';
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case COMPLETED:
        return 'badge-success';
      case 'Submitted':
      case IN_PROGRESS_STATUS:
        return 'badge-progress';
      case 'Assigned':
        return 'badge-assigned';
      case 'Unassigned':
        return 'badge-unassigned';
      case NO_CHANGE_REQUIRED:
        return 'badge-no-change-required';
      case NOT_RESPONSIBLE:
        return 'badge-not-responsible';
      case PENDING_RRE_REVIEW:
        return 'badge-danger';
      case PENDING_RAES_REVIEW:
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  }

  getAssignedName(project: ProjectRecord): string {
    if (!project.assignedTo) {
      return 'Unassigned';
    }

    const member = this.departmentMembers.find(item => item.id === project.assignedTo);
    return member ? member.name : 'Unknown';
  }

  getManagerName(project: ProjectRecord): string {
    if (!project.managerId) {
      return 'Not assigned';
    }

    const manager = this.departmentMembers.find(item => item.id === project.managerId);
    return manager ? manager.name : 'Unknown';
  }

  getWorkflowProgressPercent(project: ProjectRecord): number {
    const completedSteps = this.getWorkflowFlags(project).filter(Boolean).length;
    return Math.round((completedSteps / this.workflowSteps.length) * 100);
  }

  getWorkflowProgressBarWidth(project: ProjectRecord): number {
    const flags = this.getWorkflowFlags(project);
    const total = this.workflowSteps.length;
    const completedSteps = flags.filter(Boolean).length;
    if (completedSteps === 0) {
      return 0;
    }
    if (completedSteps === total) {
      return 100;
    }
    // Each dot center is at (i + 0.5) / total * 100
    // Bar fills to midpoint between last completed dot and next pending dot
    const lastCompletedCenter = (completedSteps - 0.5) / total * 100;
    const nextPendingCenter = (completedSteps + 0.5) / total * 100;
    return Math.round((lastCompletedCenter + nextPendingCenter) / 2);
  }

  getWorkflowStepClass(project: ProjectRecord, stepIndex: number): string {
    const flags = this.getWorkflowFlags(project);
    const currentStepIndex = flags.findIndex(flag => !flag);

    if (flags[stepIndex]) {
      return 'workflow-step--done';
    }

    if (currentStepIndex === stepIndex) {
      return 'workflow-step--current';
    }

    return 'workflow-step--pending';
  }

  iconType(fileName: string): string {
    if (!fileName) {
      return 'default';
    }
    const name = String(fileName).toLowerCase();
    if (name.endsWith('.pdf')) {
      return 'pdf';
    }
    if (name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.xlsm')) {
      return 'xls';
    }
    if (name.endsWith('.doc') || name.endsWith('.docx')) {
      return 'doc';
    }
    if (name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg')) {
      return 'image';
    }
    if (name.endsWith('.csv')) {
      return 'csv';
    }
    
    return 'default';
  }

  private getWorkflowFlags(project: ProjectRecord): boolean[] {
  // Reopened Manage Approval link after the request was already actioned: keep the
  // workflow exactly as it looked when the link was first opened (pending RRE review).
  if (this.fromManageApproval && this.alreadyActionedOnOpen) {
    return [true, true, false, false, false];
  }

  const isAssigned = !!project.assignedTo && project.assignedTo.trim() !== '';

  const isInProgress = (project.subTaskStatus || '').toLowerCase() === IN_PROGRESS;

  const isPendingManagerApprovalOrBeyond = 
    project.status === PENDING_RRE_REVIEW || 
    project.status === PENDING_RAES_REVIEW || 
    project.status === 'Completed';
  // After a rework re-submission, project.status may still be 'In Progress' but
  // subTaskStatus reflects the actual RRE workflow state ('Pending RRE Review' etc.)
  const isSubTaskPendingReviewOrBeyond =
    project.subTaskStatus === PENDING_RRE_REVIEW ||
    project.subTaskStatus === 'APPROVED' ||
    project.subTaskStatus === PENDING_RAES_REVIEW;

  // Rework state: sub_task_status 'In Progress' with an assigned RRE.
  // Step 2 only lights up here if document statuses are actually present
  // (i.e. resubmitted after rework). If statuses were reset (null), show only 20%.
  const isReworkStateWithStatus =
    isInProgress && !!project.managerId &&
    (this.allDocumentsHaveStatus(project) || !!project.noDocumentUploadsStatus);

  const hasAllDocumentsWithStatus = 
    (isAssigned && this.allDocumentsHaveStatus(project)) || 
    (isAssigned && !!project.noDocumentUploadsStatus) ||
    this.statusSubmittedForApproval || 
    isPendingManagerApprovalOrBeyond ||
    isSubTaskPendingReviewOrBeyond ||
    isReworkStateWithStatus;
  
  // rreAssigned (dot 3) lights up only when RRE has approved and status moves to
  // 'Pending RAES Review' or beyond — giving 60% (3/5). While status is 'Pending RRE Review'
  // only 2 dots are lit (40%).
 
  const isSubTaskCompleted = project.subTaskStatus === 'Completed';
  const rreAssigned = this.fromManageApproval
    ? (project.status === PENDING_RAES_REVIEW || project.status === 'Completed' || isSubTaskCompleted)
    : (project.subTaskStatus === 'APPROVED' ||
       project.subTaskStatus === PENDING_RAES_REVIEW ||
       project.status === PENDING_RAES_REVIEW ||
       project.status === 'Completed' ||
       isSubTaskCompleted);
  const raesAssigned =
    project.status === 'Completed' || isSubTaskCompleted;
  const completed =
    project.status === 'Completed' || isSubTaskCompleted;
    
  return [
    isAssigned,
    hasAllDocumentsWithStatus,
    rreAssigned,
    raesAssigned,
    completed
  ];
}

  private allDocumentsHaveStatus(project: ProjectRecord): boolean {
    if (!project.uploadedFilesData || project.uploadedFilesData.length === 0) {
      return false;
    }
    const validStatuses = new Set([NO_CHANGE_REQUIRED, NOT_RESPONSIBLE, COMPLETED]);
    
    return project.uploadedFilesData.every(file => {
      const status = this.getDocumentMarkStatus(project, file.fileName);
      return status && validStatuses.has(status);
    });
  }

  

  private hasUploadedDocuments(project: ProjectRecord): boolean {
    if (project.uploadedFilesData && project.uploadedFilesData.length > 0) {
      return true;
    }
    if (project.uploadedDocuments && project.uploadedDocuments.length > 0) {
      return true;
    }
    this.ensureDocumentUploadMap(project);
    const map = project.uploadedDocumentsByAdminDoc;
    if (map) {
      return Object.values(map).some(files => files.length > 0);
    }

    return false;
  }

  private ensureDocumentUploadMap(project: ProjectRecord): void {
    if (!project.uploadedDocumentsByAdminDoc) {
      project.uploadedDocumentsByAdminDoc = {};
    }

    project.adminDocuments.forEach((documentName, index) => {
      if (!project.uploadedDocumentsByAdminDoc || project.uploadedDocumentsByAdminDoc[documentName]) {
        return;
      }

      const fallbackUploadedDocument = project.uploadedDocuments[index];
      project.uploadedDocumentsByAdminDoc[documentName] = fallbackUploadedDocument ? [fallbackUploadedDocument] : [];
    });
  }

  private ensureDocumentStatusMap(project: ProjectRecord): void {
    if (!project.documentStatusByAdminDoc) {
      project.documentStatusByAdminDoc = {};
    }

    project.adminDocuments.forEach(documentName => {
      if (!project.documentStatusByAdminDoc || project.documentStatusByAdminDoc[documentName] !== undefined) {
        return;
      }

      project.documentStatusByAdminDoc[documentName] = '';
    });
  }

  fetchActivityStream(projectId: string, departmentId: string): void {
    this.activityStreamData = null;
    this.dashboardService.fetchActivityStream(projectId, departmentId).subscribe({
      next: (response: any) => {
        const data = response?.payload ?? response;
        this.activityStreamData = Array.isArray(data) ? data : [];
        console.info('Activity stream data:', this.activityStreamData);
      },
      error: (error: any) => {
        console.trace('Error fetching activity stream:', error);
        this.activityStreamData = [];
      }
    });
  }

 

getActivityIconType(activity: Activity): string {
  const type = String(activity.activity_type ?? activity.activityType ?? '').toLowerCase();
  const description = String(activity.activity_description ?? '').toLowerCase();

  if (type === 'comment' || this.matches(description, ['comment has been added'])) {
    return 'comment';
  }

  if (this.matches(type, ['assign']) || this.matches(description, ['task assigned'])) {
    return 'assigned';
  }

  if (
    (this.matches(type, ['rre']) || this.matches(description, ['rre'])) &&
    (this.matches(type, ['reject', 'rework', 'review']) ||
     this.matches(description, ['reject', 'rework', 'review']))
  ) {
    return 'rre-review';
  }

  if (
    (type === 'no changes required' ||
    type === 'not responsible' ||
    (type === 'completed' && this.matches(description, ['status updated', 'status changed'])) ||
    this.matches(description, ['status updated to', 'status changed to', 'has been updated to'])) &&
    !this.matches(type, ['rework', 'reject']) && !this.matches(description, ['rework', 'reject'])
  ) {
    return 'status-update';
  }

  const rules = [
    { key: 'delete', match: ['delete'] },
    { key: 'upload', match: ['upload', 'document'] },
    { key: 'completed', match: ['approv', 'complete'] },
    { key: 'rework', match: ['rework', 'reject'] },
    { key: 'reminder', match: ['reminder', 'email'] },
    { key: 'escalation', match: ['escalat'] },
    { key: 'skip', match: ['skip'] }
  ];

  for (const rule of rules) {
    if (this.matches(type, rule.match) || this.matches(description, rule.match)) {
      return rule.key;
    }
  }

  if (this.hasComments(activity)) {
    return 'comment';
  }

  return 'activity';
}

private matches(value: string, keywords: string[]): boolean {
  return keywords.some(k => value.includes(k));
}

  hasComments(activity: any): boolean {
    return !!(activity.user_comments || activity.userComments);
  }

  
  getSortedActivities(): any[] {
    if (!this.activityStreamData || this.activityStreamData.length === 0) {
      return [];
    }
    return [...this.activityStreamData].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }

  postActivityComment(): void {
    const comment = this.newActivityComment?.trim();
    if (comment) {
      const request = {
        projectId: this.apiProjectId || String(this.projectId),
        departmentId: this.departmentId,
        projectSubTaskId: this.projectDetail?.projectSubTaskId,
        userComments: comment,
        userName: this.userDashboardService.userName
      };

      this.dashboardService.postActivityComments(this.roleviewservice.getWorkdayId(), request).subscribe({
        next: () => {
          const newActivity = {
            activity_type: 'Comment',
            activity_description: 'Comment has been added',
            user_comments: comment,
            created_by: this.userDashboardService.userName,
            created_at: new Date().toISOString(),
            department_id: this.departmentId,
            user_name: this.userDashboardService.userName
          };

          if (this.activityStreamData) {
            this.activityStreamData.unshift(newActivity);
          } else {
            this.activityStreamData = [newActivity];
          }

          this.newActivityComment = '';
          this.showSuccessMsg('Comment added successfully');
        },
        error: () => {
          this.showErrorMsg('Failed to add comment. Please try again.');
        }
      });
    }
  }

  showSuccessMsg(msg: string): void {
    this.notificationService.showSuccess(msg);
  }

  showErrorMsg(msg: string): void {
    this.notificationService.showError(msg);
  }

  getUserInitials(name: string): string {
    if (!name) {
      return '?';
    }
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  getActivityTypeLabel(activity: any): string {
    const type = this.getActivityIconType(activity);
    const labelMap: { [key: string]: string } = {
      'assigned': 'Assigned',
      'completed': 'Completed',
      'upload': 'Upload',
      'delete': 'Delete',
      'rework': 'Rework',
      'rre-review': RRE_REVIEW,
      'comment': 'Comment',
      'escalation': 'Escalation',
      'reminder': 'Reminder',
      'skip': 'Skipped',
      'status-update': 'Status Update',
      'activity': 'Activity'
    };
    return labelMap[type] || 'Activity';
  }

  private pluralize(count: number, unit: string): string {
    return `${count} ${count === 1 ? unit : unit + 's'} ago`;
  }

  getRelativeTime(dateString: string): string {
    if (!dateString) {
      return '';
    }
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      return this.pluralize(Math.floor(diffInSeconds / 60), 'minute');
    } else if (diffInSeconds < 86400) {
      return this.pluralize(Math.floor(diffInSeconds / 3600), 'hour');
    } else if (diffInSeconds < 604800) {
      return this.pluralize(Math.floor(diffInSeconds / 86400), 'day');
    } else if (diffInSeconds < 2592000) {
      return this.pluralize(Math.floor(diffInSeconds / 604800), 'week');
    } else if (diffInSeconds < 31536000) {
      return this.pluralize(Math.floor(diffInSeconds / 2592000), 'month');
    } else {
      return this.pluralize(Math.floor(diffInSeconds / 31536000), 'year');
    }
  }

  onApproveFromManageApproval(): void {
    this.openApprovalDialog('approved');
  }

  onRejectFromManageApproval(): void {
    this.openApprovalDialog('rework');
  }

  private openApprovalDialog(mode: 'approved' | 'rework'): void {
    this.approvalDialogMode = mode;
    this.approvalComment = '';
    this.showApprovalDialog = true;
  }

  cancelApprovalDialog(): void {
    this.showApprovalDialog = false;
  }

  dismissAlreadyApprovedDialog(): void {
    this.showAlreadyApprovedDialog = false;
    // Take the RRE straight to this project's own detail screen (in the plain,
    // non-manage-approval view) instead of the generic dashboard list, so they
    // land somewhere useful rather than having to search for it again.
    this.router.navigate(
      ['/user-dashboard/project', this.projectId, { departmentId: this.departmentId }],
      { queryParams: { fromManageApproval: false } }
    );
  }

  // True once the RRE stage is already past - covers the optimistic client-side
  // value ('APPROVED') set right after approving, the actual persisted backend
  // value returned on a later/fresh fetch (e.g. re-opening an old email link),
  // which reports the next-stage status directly ('Pending RAES Review' /
  // 'Completed') rather than 'APPROVED', and the rework/rejected state (subtask
  // back to 'In Progress' with an RRE already assigned).
  isRreActionsDisabled(project: any): boolean {
    if (!project) {
      return true;
    }
    return project.subTaskStatus === 'APPROVED' ||
      project.subTaskStatus === 'REJECTED' ||
      project.subTaskStatus === PENDING_RAES_REVIEW ||
      project.subTaskStatus === 'Completed' ||
      project.status === PENDING_RAES_REVIEW ||
      project.status === 'Completed' ||
      this.isReworkState(project);
  }

  // Rejected/rework state: sub_task_status reverts to 'In Progress' once an RRE
  // has already been assigned - i.e. the RRE already reviewed and sent it back.
  isReworkState(project: any): boolean {
    return (project?.subTaskStatus || '').toLowerCase() === IN_PROGRESS && !!project?.managerId;
  }

  closeApprovalResultDialog(): void {
    const wasReviewed = this.approvalResultType === 'approved';
    this.showApprovalResultDialog = false;
    this.approvalResultType = null;
    if (wasReviewed) {
      this.fetchUserProjectDetails(String(this.projectId), this.projectDetail?.departmentId || this.departmentId, this.userId);
    }
    this.router.navigate(['/user-dashboard'], { queryParams: { departmentId: this.departmentId } });
  }

  confirmApprovalDialog(): void {
    if (!this.projectDetail || !this.approvalDialogMode) {
      this.showApprovalDialog = false;
      return;
    }

    const trimmedComment = (this.approvalComment || '').trim();
    if (this.approvalDialogMode === 'rework' && !trimmedComment) {
      // Comment is mandatory for rejection; keep dialog open
      return;
    }

    const subTaskStatus: 'APPROVED' | 'REWORK' = this.approvalDialogMode === 'approved' ? 'APPROVED' : 'REWORK';

    const payload: any = {
      userId: this.userDashboardService.userId,
      projectId: String(this.projectId),
      projectName: this.projectDetail?.name || '',
      departmentId: this.projectDetail.departmentId || this.departmentId,
      departmentName: this.projectDetail?.departmentAssigned || '',
      collectionName: this.projectDetail?.collection || '',
      dueDate: this.formatDate(this.projectDetail?.dueDate || ''),
      userComments: trimmedComment,
      ccAddress: [],
      userIdentifier: this.userDashboardService.userEmail,
      requestType: 'RRE',
      rreEmailAddress:  this.userDashboardService.userEmail ,
      rreId:this.userDashboardService.userId ,
      rreName: this.userDashboardService.userName ,
      contactPerson: this.projectDetail.contactPerson ,
      subTaskStatus
    };

    console.info("approve" , payload)

    // Add no_doc_uploads_status only when rejecting (rework)
    if (this.approvalDialogMode === 'rework') {
      payload.no_doc_uploads_status = 'Not Started';
    }
    this.userDashboardService.approveRejectProjectSubTaskStatus(payload).subscribe({
      next: (response: any) => {
        if (this.approvalDialogMode === 'approved') {
          this.projectDetail.status = PENDING_RAES_REVIEW;
          this.projectDetail.subTaskStatus = 'APPROVED';
          this.approvalResultType = 'approved';
          
          
        const updatedRow = response?.payload?.updatedRow?.[0];

if (updatedRow?.updated_by) {
  const selectedProject = this.dashboardService.getSelectedProject();

  if (selectedProject?.departments) {
    const dept = selectedProject.departments.find(
      (d: any) => d.department_id === (this.projectDetail?.departmentId || this.departmentId)
    );

  
    if (dept && !dept.sub_task_updated_by) {
      dept.sub_task_updated_by = updatedRow.updated_by;
    }
  }
}
        } else {
          this.projectDetail.status = IN_PROGRESS_STATUS;
          this.projectDetail.subTaskStatus = IN_PROGRESS_STATUS;
          this.projectDetail.noDocumentUploadsStatus = 'Not Started';
          this.approvalResultType = 'rework';
        }

        this.showApprovalDialog = false;
        this.showApprovalResultDialog = true;
      },
      error: (err) => {
        console.trace('Failed to submit approval/rejection:', err);
      }
    });
  }

  private async _getFileArrayBuffer(file: any): Promise<ArrayBuffer | null> {
    // Local file
    if (file.file && typeof file.file.arrayBuffer === 'function') {
      return file.file.arrayBuffer();
    }

    const id = file.file_versioning_id;
    const workDayId = this.roleviewservice.getWorkdayId();
    if (!id || !workDayId) {
      return null;
    }

    try {
      const res: any = await new Promise((resolve, reject) => {
        this.createProjectService
          .readDocument(workDayId, id)
          .subscribe({ next: resolve, error: reject });
      });

      const body = res?.body;
      if (!body) {
        return null;
      }

      // Base64 from backend
      if (res?.isBase64Encoded || (typeof body === 'string' && !body.startsWith('http'))) {
        const base64 = body.includes(',') ? body.split(',')[1] : body;
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++){
           bytes[i] = binary.codePointAt(i);
        }
        return bytes.buffer;
      }

      // Direct URL
      if (typeof body === 'string' && body.startsWith('http')) {
        const response = await fetch(body);
        if (!response.ok) {
          throw new Error('Failed to fetch file');
        }
        return await response.arrayBuffer();
      }
    } catch (err) {
      console.trace('File fetch failed:', err);
    }

    return null;
  }

  async preview(file: any) {
    if (!file) {
      return;
    }

    const name = (file.file_name || '').toLowerCase();
    const isImage = name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') ||
      (file.type && String(file.type).toLowerCase().startsWith('image/'));

    if (name.endsWith('.pdf')) {
      await this._previewPdf(file);
    } else if (isImage) {
      await this._previewImage(file, name);
    } else if (name.endsWith('.docx')) {
      await this._previewDocx(file);
    } else if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.xlsm') || name.endsWith('.csv')) {
      await this._previewSpreadsheet(file, name);
    } else {
      alert('File type not supported for preview');
    }
  }

  private async _previewPdf(file: any): Promise<void> {
    const buffer = await this._getFileArrayBuffer(file);
    if (!buffer) {
      alert('Unable to load PDF');
      return;
    }
    const url = globalThis.URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
    this._objectUrls.push(url);
    globalThis.open(url, '_blank');
  }

  private async _previewImage(file: any, name: string): Promise<void> {
    const buffer = await this._getFileArrayBuffer(file);
    if (!buffer) {
      alert('Unable to load image');
      return;
    }
    const mime = name.endsWith('.png') ? 'image/png' : 'image/jpeg';
    const url = URL.createObjectURL(new Blob([buffer], { type: mime }));
    this._objectUrls.push(url);
    this.previewTitle = file.file_name;
    this.previewType = 'image';
    this.previewUrl = url;
    this.isPreviewOpen = true;
    this._addPreviewHistoryEntry();
  }

  private async _previewDocx(file: any): Promise<void> {
    const buffer = await this._getFileArrayBuffer(file);
    if (!buffer) {
      alert('Unable to load DOCX');
      return;
    }
    this.previewTitle = file.file_name;
    this.previewType = 'docx';
    this.isPreviewOpen = true;
    this._addPreviewHistoryEntry();
    setTimeout(async () => {
      if (!this.docxContainerRef?.nativeElement) {
        console.trace('DOCX container not available');
        return;
      }
      this.docxContainerRef.nativeElement.innerHTML = '';
      try {
        await renderAsync(buffer, this.docxContainerRef.nativeElement, undefined, { className: 'docx' });
      } catch (err) {
        console.trace('DOCX render failed', err);
      }
    }, 0);
  }

  private async _previewSpreadsheet(file: any, name: string): Promise<void> {
    const buffer = await this._getFileArrayBuffer(file);
    if (!buffer) {
      alert('Unable to load file');
      return;
    }
    this.previewTitle = file.file_name;
    this.previewType = 'xls';
    this.isPreviewOpen = true;
    this._addPreviewHistoryEntry();
    try {
      const workbook = name.endsWith('.csv')
        ? XLSX.read(new TextDecoder('utf-8').decode(buffer), { type: 'string' })
        : XLSX.read(buffer, { type: 'array' });
      this.workbookCache = workbook;
      this.sheetNames = workbook.SheetNames || [];
      this.selectedSheetIndex = 0;
      this.renderSheetByIndex(this.selectedSheetIndex);
    } catch (err) {
      console.trace('Spreadsheet/CSV render failed', err);
    }
  }

  renderSheetByIndex(index: number) {
    try {
      if (!this.workbookCache?.SheetNames || !this.workbookCache?.Sheets) {
        this.excelHtml = '<div class="xlsx-message">No workbook loaded.</div>';
        return;
      }
      const name = this.workbookCache.SheetNames[index];
      if (!name) {
        this.excelHtml = '<div class="xlsx-message">Sheet not found.</div>';
        return;
      }
      const sheet = this.workbookCache.Sheets[name];
      const tableHtml = XLSX.utils.sheet_to_html(sheet);
      const wrapped = `<div class="xlsx-preview">${tableHtml}</div>`;
      this.excelHtml = wrapped;
    } catch (err) {
      console.trace('renderSheetByIndex failed', err);
      this.excelHtml = '<div class="xlsx-message">Unable to render sheet.</div>';
    }
  }

  closePreview() {
    this.isPreviewOpen = false;
    this.previewType = null;
    this.previewUrl = null;
    this.excelHtml = '';
    this._wvInstance = null; // container removed by *ngIf; release reference
    if (this._previewHistoryState) {
      try {
        if (this._onPopState) {
          globalThis.removeEventListener('popstate', this._onPopState);
          this._onPopState = null;
        }
        this._ignoreNextPop = true;
        globalThis.history.back();
        this._previewHistoryState = false;
      } catch (e) { console.trace('closePreview history.back() failed', e); }
    }
  }

  private _addPreviewHistoryEntry() {
    try {
      if (this._previewHistoryState) {
        return;
      }
      history.pushState({ cert_ui_preview: true }, '');
      this._previewHistoryState = true;
      this._onPopState = (_ev: PopStateEvent) => {
        if (this._ignoreNextPop) { this._ignoreNextPop = false; return; }
        if (this.isPreviewOpen) {
          this.isPreviewOpen = false;
          this.previewType = null;
          this.previewUrl = null;
          this.excelHtml = '';
          if (this._onPopState) {
            globalThis.removeEventListener('popstate', this._onPopState);
            this._onPopState = null;
          }
          this._previewHistoryState = false;
        }
      };
      globalThis.addEventListener('popstate', this._onPopState);
    } catch (e) {
      console.info('History push for preview failed', e);
    }
  }

  ngOnDestroy() {
    for (const url of this._objectUrls) {
      try { globalThis.URL.revokeObjectURL(url); } catch (e) { console.trace('revokeObjectURL failed', e); }
    }
    this._objectUrls = [];
    try {
      if (this._onPopState) {
        globalThis.removeEventListener('popstate', this._onPopState);
        this._onPopState = null;
      }
      if (this._previewHistoryState) {
        this._ignoreNextPop = true;
        try { history.back(); } catch (e) { console.trace('ngOnDestroy history.back() failed', e); }
        this._previewHistoryState = false;
      }
    } catch (e) { console.trace('ngOnDestroy cleanup error', e); }
    clearTimeout(this.debounceTimer);
  }

  reloadPage(): void {
    this.fetchUserProjectDetails(String(this.projectId), this.departmentId, this.userId);
  }
}